import { useEffect } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import type { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AxiosError } from 'axios';

import type { CreatePasswordFormProps } from '@features/index';

import type { AgentRegistrationFormProps } from '@entities/index';

import {
  AnalyticEvent,
  BoundaryErrorType,
  FlkCode,
  getMismatches,
  sendRequestErrorsAnalytics,
} from '@shared/index';
import { type CommonResponse } from '@shared/index';

interface UseHandleOtpCodeErrors {
  error: AxiosError<CommonResponse> | null;
  setError: UseFormSetError<
    AgentRegistrationFormProps & CreatePasswordFormProps
  >;
  handleSpecificCodeErrors: { [code: string]: () => void };
}

export const useHandleRegistrationCodeErrors = ({
  error,
  setError,
  handleSpecificCodeErrors,
}: UseHandleOtpCodeErrors) => {
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    if (!error) return;

    const mismatches = getMismatches(error);
    sendRequestErrorsAnalytics(AnalyticEvent.AGENT_REG_ERROR, mismatches);

    if (!!mismatches) {
      let hasKnownErrors = false;
      const passwordValidationErrorMessages: string[] = [];
      mismatches.forEach(({ code, message }) => {
        const handleSpecificCodeError = handleSpecificCodeErrors[code];

        if (handleSpecificCodeError) {
          handleSpecificCodeError();
          hasKnownErrors = true;
        } else if (
          code === FlkCode.RMM_6010_0003 ||
          code === FlkCode.RMM_6010_0006 ||
          code === FlkCode.RMM_6010_0007
        ) {
          // Неккоректный телефон
          setError('phone', {
            message: t('COMMON:errors.incorrectPhoneNumber'),
          });
          hasKnownErrors = true;
        } else if (
          code === FlkCode.RMM_6010_0004 ||
          code === FlkCode.RMM_6010_0012 ||
          code === FlkCode.RMM_6010_0013
        ) {
          // Неккоректный пароль
          setError('password', {
            message: t('PASSWORD:errors.invalidCharacters'),
          });
          hasKnownErrors = true;
        } else if (
          code === FlkCode.RMM_6010_0016 ||
          code === FlkCode.RMM_6010_0019 ||
          code === FlkCode.RMM_6010_0024 ||
          code === FlkCode.RMM_6010_0025 ||
          code === FlkCode.RMM_6010_0026
        ) {
          if (message) passwordValidationErrorMessages.push(message);
          hasKnownErrors = true;
        }
      });

      if (passwordValidationErrorMessages.length) {
        setError('root.validationError', {
          message: passwordValidationErrorMessages.join('\n'),
        });
      }

      if (!hasKnownErrors) {
        showBoundary(BoundaryErrorType.SomethingWentWrong);
      }
    } else {
      showBoundary(BoundaryErrorType.SomethingWentWrong);
    }
  }, [error, handleSpecificCodeErrors, setError]);
};
